import { useEffect } from 'react';
import { contrastRatio, fitContrast, lightnessAndChroma, shade } from '@kanos/domain';
/** The backgrounds an accent sits on: page and opaque surface (theme.css `--bg`, `--surface`). */
export const ACCENT_BACKGROUNDS = {
    light: ['#ffffff'],
    dark: ['#050608', '#121317'],
};
const TEXT = 4.5;
const WHITE = '#ffffff';
const BLACK = '#000000';
const MARK = 3;
/** The second accent: the firm's hue turned by 40°, close enough to read as one family. */
const NEIGHBOUR = (-40 * Math.PI) / 180;
function tokensFor(hex, theme) {
    const backgrounds = ACCENT_BACKGROUNDS[theme];
    const towards = theme === 'light' ? 'dark' : 'light';
    const { c } = lightnessAndChroma(hex);
    // A faint wash of the hue: light enough to carry dark text, or dark enough for light text.
    const accentSoft = theme === 'light'
        ? shade(hex, { l: 0.95, c: Math.min(c * 0.35, 0.05) })
        : shade(hex, { l: 0.27, c: Math.min(c * 0.5, 0.08) });
    const neighbour = shade(hex, { turn: NEIGHBOUR });
    const accent = fitContrast(hex, backgrounds, MARK, towards);
    return {
        accent,
        accentText: fitContrast(hex, [...backgrounds, accentSoft], TEXT, towards),
        accentOn: contrastRatio(WHITE, accent) >= contrastRatio(BLACK, accent) ? WHITE : BLACK,
        accentSoft,
        accent2: fitContrast(neighbour, backgrounds, MARK, towards),
        accent2Text: fitContrast(neighbour, backgrounds, TEXT, towards),
    };
}
/** The accent tokens of both themes for a firm colour `#rrggbb`. */
export function accentTokens(hex) {
    return { light: tokensFor(hex, 'light'), dark: tokensFor(hex, 'dark') };
}
const VARIABLES = {
    accent: '--firm-accent',
    accentText: '--firm-accent-text',
    accentOn: '--firm-accent-on',
    accentSoft: '--firm-accent-soft',
    accent2: '--firm-accent-2',
    accent2Text: '--firm-accent-2-text',
};
/**
 * The CSS custom properties that carry a firm colour into theme.css, e.g. for the `style` of a
 * preview; `null` gives none, so the defaults apply.
 */
export function accentProperties(hex) {
    if (hex === null)
        return {};
    const tokens = accentTokens(hex);
    const properties = {};
    for (const theme of ['light', 'dark']) {
        for (const [key, name] of Object.entries(VARIABLES)) {
            properties[`${name}-${theme}`] = tokens[theme][key];
        }
    }
    return properties;
}
/**
 * Applies the firm colour to the whole page (custom properties on `<html>`), or the defaults
 * for `null`; switching light and dark needs nothing more, both are set at once.
 */
export function useAccent(hex) {
    useEffect(() => {
        const style = document.documentElement.style;
        const properties = accentProperties(hex);
        for (const [name, value] of Object.entries(properties))
            style.setProperty(name, value);
        return () => {
            for (const name of Object.keys(properties))
                style.removeProperty(name);
        };
    }, [hex]);
}
//# sourceMappingURL=accent.js.map